import React, { useState, useEffect } from "react";
import "./App.css";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/LoginPage";
import Navbar from "@/components/Navbar";
import TradeForm from "@/components/TradeForm";
import StatsDashboard from "@/components/StatsDashboard";
import TradeHistory from "@/components/TradeHistory";
import CoachRecap from "@/components/CoachRecap";
import { Loader2, Activity } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("UI Error caught:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 max-w-xl mx-auto my-12 bg-slate-900 border border-rose-500 rounded-xl text-center space-y-4">
          <h2 className="text-xl font-bold text-rose-400">Something went wrong in the app UI</h2>
          <p className="text-xs text-slate-300 font-mono bg-slate-950 p-3 rounded">
            {this.state.error?.toString()}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-cyan-500 text-slate-950 font-bold text-xs rounded"
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Loading splash (while checking cookie on startup) ────────────────────────
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#050505] grid-bg flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-14 h-14 flex items-center justify-center border border-[#00F0FF]/60 glow-cyan">
          <Activity className="w-7 h-7 text-[#00F0FF]" />
        </div>
        <Loader2 className="w-5 h-5 text-[#00F0FF] animate-spin" />
        <div className="text-[11px] text-[#848D97] tracking-[0.3em] uppercase">Verifying session…</div>
      </div>
    </div>
  );
}

// ─── Main shell (only shown when authenticated) ────────────────────────────────
function AppShell() {
  const { user, logout, loading } = useAuth();
  const [view, setView] = useState("new");
  const [refreshKey, setRefreshKey] = useState(0);

  const bump = () => setRefreshKey((k) => k + 1);

  // 30-Minute Inactivity Auto-Logout (Tab-background resilient)
  useEffect(() => {
    if (!user) return;

    const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes
    let lastActivity = Date.now();

    const recordActivity = () => {
      lastActivity = Date.now();
    };

    const checkInactivity = () => {
      if (Date.now() - lastActivity >= INACTIVITY_LIMIT) {
        logout();
        toast.info("Signed out automatically after 30 minutes of inactivity.");
      }
    };

    // Periodic check every 15 seconds
    const interval = setInterval(checkInactivity, 15000);

    // Check immediately when user switches back to tab or focuses window
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkInactivity();
      }
    };

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, recordActivity));
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      events.forEach((ev) => window.removeEventListener(ev, recordActivity));
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user, logout]);

  if (loading) return <LoadingScreen />;
  if (!user) return <LoginPage />;

  return (
    <div className="App scanlines min-h-screen bg-[#050505] text-[#E2E8F0]">
      <div className="min-h-screen grid-bg">
        <Navbar view={view} setView={setView} user={user} onLogout={logout} />
        <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
          <ErrorBoundary>
            <div className={view === "new" ? "block" : "hidden"}>
              <TradeForm onLogged={() => { bump(); setView("history"); }} />
            </div>
            <div className={view === "dashboard" ? "block" : "hidden"}>
              <StatsDashboard refreshKey={refreshKey} />
            </div>
            <div className={view === "history" ? "block" : "hidden"}>
              <TradeHistory refreshKey={refreshKey} onChange={bump} />
            </div>
            <div className={view === "coach" ? "block" : "hidden"}>
              <CoachRecap />
            </div>
          </ErrorBoundary>
        </main>
        <footer className="border-t border-[#1A1A24] py-6 text-center text-xs text-[#848D97]">
          ICC JOURNAL // INDICATION · CORRECTION · CONTINUATION · Inspired by Sci
        </footer>
      </div>
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0C0C0E",
            border: "1px solid #1A1A24",
            color: "#E2E8F0",
            fontFamily: "JetBrains Mono, monospace",
          },
        }}
      />
    </div>
  );
}

// ─── Root ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
